import React, { FC, useEffect, useMemo, useState } from "react";
import apiClient from "../utils/apiClient";
import { useSelector } from "react-redux";
import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import Provider from "../enum/Provider.enum";
import { RootState } from "../store";

type CreateLinkFormProps = {
  onClose: () => void;
  onSuccess: (link: string) => void;
};

const CreateLinkForm: FC<CreateLinkFormProps> = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();

  const { provider } = useSelector((state: RootState) => state.app);
  const [schemes, setSchemes] = useState([]);
  const [selectedSite, setSelectedSite] = useState<string>("");
  const [selectedSection, setSelectedSection] = useState<string>("");
  const [linkName, setLinkName] = useState<string>("");
  const [utmSource, setUtmSource] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [utmSourceError, setUtmSourceError] = useState("");
  const shouldShowUtmSource = provider === Provider.BELARUS;

  const handleUtmSourceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUtmSource(value);

    const regex = /^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/;
    if (value && !regex.test(value)) {
      setUtmSourceError(t("createLink.validation.utmSource"));
    } else {
      setUtmSourceError("");
    }
  };

  useEffect(() => {
    const fetchSchemes = async () => {
      try {
        const response = await apiClient.get("/links-schemes");
        setSchemes(response.data.select || []);
      } catch (err) {
        console.error("Ошибка при загрузке схем:", err);
      }
    };
    fetchSchemes();
  }, []);

  const groupedSchemes = useMemo(() => {
    const groups: any = {};
    schemes.forEach((scheme: any) => {
      const parts = scheme.title.split("|");
      const site = parts[2] || "";
      const section = parts[3] || "";
      if (!groups[site]) {
        groups[site] = new Set();
      }
      groups[site].add(section);
    });
    Object.keys(groups).forEach((site) => {
      groups[site] = Array.from(groups[site]);
    });
    return groups;
  }, [schemes]);

  const getSelectedSchemeId = () => {
    const scheme: any = schemes.find((scheme: any) => {
      const parts = scheme.title.split("|");
      return parts[2] === selectedSite && parts[3] === selectedSection;
    });
    return scheme ? scheme?.ID : null;
  };

  const handleGenerateLink = async () => {
    const schemeId = getSelectedSchemeId();
    if (!schemeId) {
      console.error("Не найдена схема для выбранного сайта и раздела");
      return;
    }

    try {
      setIsSaving(true);
      const response = await apiClient.post("/links/generate", {
        schemeId,
        linkName,
        ...(shouldShowUtmSource ? { utmSource } : {}),
      });

      console.log("Сгенерированная ссылка:", response.data.link);

      if (onSuccess) {
        onSuccess(response.data.link);
      }

      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error("Ошибка при генерации ссылки:", err);
      alert(
        (err as AxiosError<{ error?: string }>).response?.data?.error ||
          t("createLink.errors.generate"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <DialogContent dividers>
        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="link-site-label">{t("createLink.site")}</InputLabel>

            <Select
              id="link-site"
              size="small"
              value={selectedSite}
              label={t("createLink.site")}
              onChange={(e) => {
                setSelectedSite(e.target.value);
                setSelectedSection("");
              }}
              variant="outlined"
              labelId="link-site-label"
              required
              disabled={isSaving}
            >
              {Object.keys(groupedSchemes).map((siteName) => (
                <MenuItem key={siteName} value={siteName}>
                  {siteName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="link-section-label">
              {t("createLink.section")}
            </InputLabel>

            <Select
              id="link-section"
              size="small"
              value={selectedSection}
              label={t("createLink.section")}
              onChange={(e) => setSelectedSection(e.target.value)}
              variant="outlined"
              labelId="link-section-label"
              disabled={!selectedSite || isSaving}
              required
            >
              {selectedSite &&
                groupedSchemes[selectedSite] &&
                groupedSchemes[selectedSite].map((section: any) => (
                  <MenuItem key={section} value={section}>
                    {section}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label={t("createLink.linkName")}
            placeholder={t("createLink.linkNamePlaceholder")}
            value={linkName}
            fullWidth
            required
            onChange={(e) => setLinkName(e.target.value)}
            disabled={isSaving}
          />

          {shouldShowUtmSource && (
            <TextField
              size="small"
              label={t("createLink.source")}
              placeholder={t("createLink.source")}
              value={utmSource}
              fullWidth
              onChange={handleUtmSourceChange}
              disabled={isSaving}
              error={!!utmSourceError}
              helperText={utmSourceError}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateLink}
          fullWidth
          loading={isSaving}
        >
          {t("createLink.generate")}
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          fullWidth
          disabled={isSaving}
        >
          {t("createLink.cancel")}
        </Button>
      </DialogActions>
    </>
  );
};

export default CreateLinkForm;
